/**
 * パート構成定数とユーティリティ関数
 * GB組織74問・7パート形式 (gb-org-questionnaire-74)
 *
 * Part1: 1-24   (24問) binary  ハーマン相当 H-01〜H-24
 * Part2: 25-54  (30問) scale   Big5 B-01〜B-30
 * Part3: 55-56  (2問)  scale   GBスキル SK-03/SK-04
 * Part4: 57-66  (10問) scale   ONA基本 O-01〜O-10 (直前にリセット画面s4)
 * Part5: 67-68  (2問)  choice  ONA具体化 O-11/O-12
 * Part6: 69-71  (名目3) matrix 部門間マトリクス (1画面、部門1行=1問分。実数は部門数に依存)
 * Part7: 72-74  (3問)  scale   離職リスク R-01〜R-03
 *
 * 質問マスタは72問 (Part6はmatrix1問)。進捗カウンターは部門1行=1問と
 * 数えるため、名目総数74 = 71 + 部門3行。実際の分母は 71 + 部門数。
 */
import type { QuestionFormatType } from '../types/questionnaire';

// === 定数 ===

export interface PartConfig {
  start: number;   // 1-based 開始質問番号 (名目74問ベース)
  end: number;     // 1-based 終了質問番号 (名目74問ベース)
  count: number;   // 名目問数 (Part6は名目3部門)
  formatType: QuestionFormatType;
  prefix: string;
  orientation: 'portrait';
  /** このパートの直前にリセット中間画面 (モックs4) を表示する */
  resetBefore?: boolean;
}

export const PART_CONFIG: Record<number, PartConfig> = {
  1: { start: 1, end: 24, count: 24, formatType: 'binary', prefix: 'H', orientation: 'portrait' },
  2: { start: 25, end: 54, count: 30, formatType: 'scale', prefix: 'B', orientation: 'portrait' },
  3: { start: 55, end: 56, count: 2, formatType: 'scale', prefix: 'SK', orientation: 'portrait' },
  4: { start: 57, end: 66, count: 10, formatType: 'scale', prefix: 'O', orientation: 'portrait', resetBefore: true },
  5: { start: 67, end: 68, count: 2, formatType: 'choice', prefix: 'O', orientation: 'portrait' },
  6: { start: 69, end: 71, count: 3, formatType: 'matrix', prefix: 'O', orientation: 'portrait' },
  7: { start: 72, end: 74, count: 3, formatType: 'scale', prefix: 'R', orientation: 'portrait' },
} as const;

/** マスタ上の質問数毎の累積境界（0-based index）: Part6はmatrix1問として数える */
export const PART_BOUNDARIES = [0, 24, 54, 56, 66, 68, 69, 72] as const;

/** マスタ上の質問総数 (Part6のmatrixは1問) */
export const TOTAL_MASTER_QUESTIONS = 72;

/** 名目の質問総数 (Part6を3部門と仮定した表示用。実際の分母は 71 + 部門数) */
export const TOTAL_QUESTIONS = 74;

/** matrix を除いた質問数 (進捗分母のベース) */
export const NON_MATRIX_QUESTIONS = 71;

/** 推定回答時間（分） */
export const ESTIMATED_TIME_MINUTES = 16;

// === ユーティリティ関数 ===

/**
 * 0-basedマスタ質問インデックスからパート番号（1〜7）を返す
 */
export function getPartNumber(index: number): number {
  for (let i = 1; i < PART_BOUNDARIES.length; i++) {
    if (index < PART_BOUNDARIES[i]) return i;
  }
  return 7;
}

/**
 * 0-basedマスタ質問インデックスからパート内進捗情報を返す
 * @param departmentCount Part6の部門数 (進捗分母 71 + departmentCount の計算に使用)
 */
export function getPartProgress(index: number, departmentCount = 3): {
  partNumber: number;
  questionInPart: number;   // 1-based パート内質問番号
  partTotal: number;
  overallPercent: number;   // 0〜100
} {
  const partNumber = getPartNumber(index);
  const partStart = PART_BOUNDARIES[partNumber - 1];
  const partTotal = partNumber === 6 ? departmentCount : PART_CONFIG[partNumber].count;
  const questionInPart = index - partStart + 1;
  const displayPosition = getDisplayQuestionNumber(index, departmentCount);
  const total = getTotalDisplayQuestions(departmentCount);
  const overallPercent = Math.round((displayPosition / total) * 100);

  return { partNumber, questionInPart, partTotal, overallPercent };
}

/**
 * 進捗カウンターの分母: 71 + 部門数 (部門0件時はPart6スキップで71)
 */
export function getTotalDisplayQuestions(departmentCount: number): number {
  return NON_MATRIX_QUESTIONS + departmentCount;
}

/**
 * 0-basedマスタ質問インデックスから表示上の質問番号 (1-based) を返す
 * Part6 (matrix) は部門数分の問数を占めるため、Part7以降はオフセットされる
 * matrix画面自体は先頭行の番号 (69) を返す
 */
export function getDisplayQuestionNumber(index: number, departmentCount: number): number {
  const matrixIndex = PART_BOUNDARIES[5]; // 68 (0-based): matrix質問の位置
  if (index <= matrixIndex) {
    return index + 1;
  }
  // Part7: matrix1問を部門数に置き換えたオフセット
  return index + departmentCount; // (index + 1) - 1 + departmentCount
}

/**
 * 連続する2つの0-basedインデックスでパート遷移が発生するか判定
 */
export function isPartTransition(prevIndex: number, nextIndex: number): boolean {
  return getPartNumber(prevIndex) !== getPartNumber(nextIndex);
}

/**
 * 次のパートの直前にリセット中間画面 (モックs4) を挟むか判定
 * Part3→Part4 の遷移時のみ true
 */
export function needsResetScreen(prevIndex: number, nextIndex: number): boolean {
  return (
    isPartTransition(prevIndex, nextIndex) &&
    PART_CONFIG[getPartNumber(nextIndex)]?.resetBefore === true
  );
}
